import { useEffect, useRef, useState } from "react";

import type { PersonalCustomEmoji } from "../../api/client";
import { telegramWebApp } from "../../telegram/webapp";
import { CustomEmojiPicker } from "./CustomEmojiPicker";
import {
  parseTelegramEmojiHtml,
  plainEmojiText,
  replaceEmojiTarget,
  resolveEmojiReplacementTarget,
  serializeTelegramEmojiHtml,
  type EmojiReplacementTarget,
  type EmojiTextNode
} from "./custom-emoji-model";
import { useCustomEmojiLibrary } from "./useCustomEmojiLibrary";

type Props = {
  value: string;
  onChange: (value: string) => void;
  label: string;
  rows?: number;
  placeholder?: string;
};

const appendEditorNode = (parent: Node, node: EmojiTextNode) => {
  if (node.type === "text") {
    parent.appendChild(document.createTextNode(node.value));
    return;
  }
  const token = document.createElement("span");
  token.className = "custom-emoji-editor-token";
  token.contentEditable = "false";
  token.dataset.customEmojiId = node.customEmojiId;
  token.dataset.emojiFallback = node.fallback;
  token.setAttribute("role", "img");
  token.setAttribute("aria-label", node.fallback);
  token.textContent = node.fallback;
  parent.appendChild(token);
};

const renderValue = (root: HTMLElement, value: string) => {
  root.replaceChildren();
  parseTelegramEmojiHtml(value).forEach((node) => appendEditorNode(root, node));
};

const normalizePastedText = (value: string) => value
  .replace(/\r\n?|\u2028|\u2029/g, "\n")
  .replace(/\u00a0/g, " ")
  .replace(/[\u200B\uFEFF]/g, "")
  .replace(/[ \t]+\n/g, "\n");

const nodesFromEditor = (root: HTMLElement): EmojiTextNode[] => {
  const nodes: EmojiTextNode[] = [];
  const pushText = (value: string) => {
    if (!value) return;
    const previous = nodes.at(-1);
    if (previous?.type === "text") previous.value += value;
    else nodes.push({ type: "text", value });
  };
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node.textContent ?? "");
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    const customEmojiId = node.dataset.customEmojiId;
    const fallback = node.dataset.emojiFallback;
    if (customEmojiId && fallback) {
      nodes.push({ type: "custom_emoji", customEmojiId, fallback });
      return;
    }
    if (node.tagName === "BR") {
      pushText("\n");
      return;
    }
    const block = node !== root && (node.tagName === "DIV" || node.tagName === "P");
    node.childNodes.forEach(walk);
    if (block && nodes.length && !plainEmojiText(nodes).endsWith("\n")) pushText("\n");
  };
  root.childNodes.forEach(walk);
  return nodes;
};

const makeToken = (emoji: PersonalCustomEmoji) => {
  const token = document.createElement("span");
  token.className = "custom-emoji-editor-token";
  token.contentEditable = "false";
  token.dataset.customEmojiId = emoji.customEmojiId;
  token.dataset.emojiFallback = emoji.fallback;
  token.setAttribute("role", "img");
  token.setAttribute("aria-label", emoji.fallback);
  token.textContent = emoji.fallback;
  return token;
};

const renderedOffsetAtBoundary = (
  root: HTMLElement,
  container: Node,
  offset: number
) => {
  const prefix = document.createRange();
  prefix.selectNodeContents(root);
  try {
    prefix.setEnd(container, offset);
  } catch {
    return null;
  }
  const fragmentRoot = document.createElement("div");
  fragmentRoot.appendChild(prefix.cloneContents());
  return plainEmojiText(nodesFromEditor(fragmentRoot)).length;
};

const placeCaretAtRenderedOffset = (root: HTMLElement, renderedOffset: number) => {
  const range = document.createRange();
  const target = Math.max(0, renderedOffset);
  let cursor = 0;
  let placed = false;
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const length = child.textContent?.length ?? 0;
      if (target <= cursor + length) {
        range.setStart(child, Math.max(0, target - cursor));
        placed = true;
        break;
      }
      cursor += length;
      continue;
    }
    if (child instanceof HTMLElement && child.dataset.customEmojiId) {
      const length = child.dataset.emojiFallback?.length ?? 0;
      if (target <= cursor + length) {
        range.setStartAfter(child);
        placed = true;
        break;
      }
      cursor += length;
    }
  }
  if (!placed) {
    range.selectNodeContents(root);
    range.collapse(false);
  } else {
    range.collapse(true);
  }
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
};

type CapturedReplacement = {
  source: string;
  target: EmojiReplacementTarget;
};

export function CustomEmojiTextEditor({ value, onChange, label, rows = 6, placeholder }: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const replacementTargetRef = useRef<CapturedReplacement | null>(null);
  const pointerPreparedRef = useRef(false);
  const emittedRef = useRef(value);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectionFallback, setSelectionFallback] = useState<string | null>(null);
  const library = useCustomEmojiLibrary();

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || value === emittedRef.current && document.activeElement === editor) return;
    renderValue(editor, value);
    emittedRef.current = value;
  }, [value]);

  const emit = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const canonical = serializeTelegramEmojiHtml(nodesFromEditor(editor));
    emittedRef.current = canonical;
    onChange(canonical);
  };

  const pastePlainText = (raw: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const range = selectedRange && editor.contains(selectedRange.commonAncestorContainer)
      ? selectedRange
      : document.createRange();
    if (!selectedRange || !editor.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    range.deleteContents();
    const textNode = document.createTextNode(normalizePastedText(raw));
    range.insertNode(textNode);
    range.setStart(textNode, textNode.data.length);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRangeRef.current = range.cloneRange();
    replacementTargetRef.current = null;
    setSelectionFallback(null);
    emit();
  };

  useEffect(() => {
    const editor = editorRef.current as (HTMLDivElement & { value?: string }) | null;
    if (!editor) return;
    Object.defineProperty(editor, "value", {
      configurable: true,
      get: () => plainEmojiText(nodesFromEditor(editor)),
      set: (next: unknown) => renderValue(editor, String(next ?? ""))
    });
    const handleChange = () => emit();
    editor.addEventListener("change", handleChange);
    return () => editor.removeEventListener("change", handleChange);
  });

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return null;
    savedRangeRef.current = range.cloneRange();
    return range;
  };

  const preparePickerSelection = () => {
    const editor = editorRef.current;
    const range = rememberSelection();
    replacementTargetRef.current = null;
    if (!editor || !range) {
      setSelectionFallback(null);
      return;
    }
    const start = renderedOffsetAtBoundary(editor, range.startContainer, range.startOffset);
    const end = renderedOffsetAtBoundary(editor, range.endContainer, range.endOffset);
    if (start === null || end === null) {
      setSelectionFallback(null);
      return;
    }
    const nodes = nodesFromEditor(editor);
    const source = serializeTelegramEmojiHtml(nodes);
    const target = resolveEmojiReplacementTarget(nodes, start, end);
    if (target) {
      replacementTargetRef.current = { source, target };
      setSelectionFallback(target.fallback);
      return;
    }
    setSelectionFallback(null);
    const insertionRange = range.cloneRange();
    if (!insertionRange.collapsed) insertionRange.collapse(false);
    savedRangeRef.current = insertionRange;
  };

  const refreshSelectionHint = () => {
    const editor = editorRef.current;
    const range = rememberSelection();
    if (!editor || !range) {
      setSelectionFallback(null);
      return;
    }
    const start = renderedOffsetAtBoundary(editor, range.startContainer, range.startOffset);
    const end = renderedOffsetAtBoundary(editor, range.endContainer, range.endOffset);
    if (start === null || end === null) {
      setSelectionFallback(null);
      return;
    }
    const target = resolveEmojiReplacementTarget(nodesFromEditor(editor), start, end);
    setSelectionFallback(target?.fallback ?? null);
  };

  const openPicker = () => {
    if (!pointerPreparedRef.current) preparePickerSelection();
    pointerPreparedRef.current = false;
    setPickerOpen(true);
  };

  const selectEmoji = (emoji: PersonalCustomEmoji) => {
    const editor = editorRef.current;
    if (!editor) return;
    const captured = replacementTargetRef.current;
    const currentSource = serializeTelegramEmojiHtml(nodesFromEditor(editor));
    if (captured && captured.source === currentSource) {
      const currentNodes = parseTelegramEmojiHtml(currentSource);
      const verified = resolveEmojiReplacementTarget(
        currentNodes,
        captured.target.start,
        captured.target.end
      );
      if (
        verified &&
        verified.start === captured.target.start &&
        verified.end === captured.target.end &&
        verified.fallback === captured.target.fallback
      ) {
        const canonical = serializeTelegramEmojiHtml(replaceEmojiTarget(
          currentNodes,
          captured.target,
          { type: "custom_emoji", customEmojiId: emoji.customEmojiId, fallback: emoji.fallback }
        ));
        renderValue(editor, canonical);
        emittedRef.current = canonical;
        onChange(canonical);
        editor.focus();
        placeCaretAtRenderedOffset(editor, captured.target.start + emoji.fallback.length);
        replacementTargetRef.current = null;
        setSelectionFallback(null);
        savedRangeRef.current = window.getSelection()?.rangeCount
          ? window.getSelection()!.getRangeAt(0).cloneRange()
          : null;
        void library.markUsed(emoji);
        setPickerOpen(false);
        return;
      }
    }
    replacementTargetRef.current = null;
    setSelectionFallback(null);
    const selection = window.getSelection();
    const range = savedRangeRef.current && editor.contains(savedRangeRef.current.commonAncestorContainer)
      ? savedRangeRef.current
      : document.createRange();
    if (!savedRangeRef.current || !editor.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    if (!range.collapsed) range.collapse(false);
    range.deleteContents();
    const token = makeToken(emoji);
    const caret = document.createTextNode("");
    range.insertNode(caret);
    range.insertNode(token);
    range.setStartAfter(caret);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    editor.focus();
    savedRangeRef.current = range.cloneRange();
    emit();
    void library.markUsed(emoji);
    setPickerOpen(false);
  };

  const plainLength = plainEmojiText(parseTelegramEmojiHtml(value)).length;

  return (
    <div className="field custom-emoji-text-field">
      <span>{label}</span>
      <div className="custom-emoji-replace-guide">
        <button
          type="button"
          className="custom-emoji-open"
          aria-label={`Добавить премиум эмодзи: ${label}`}
          onPointerDown={() => {
            preparePickerSelection();
            pointerPreparedRef.current = true;
          }}
          onClick={openPicker}
        >
          <span aria-hidden="true">✨</span>
          <span>{selectionFallback
            ? `Заменить «${selectionFallback}» на Premium`
            : "Заменить эмодзи на Premium"}</span>
        </button>
        <p>{selectionFallback
          ? `Выбрано для замены: ${selectionFallback}`
          : "Поставьте курсор сразу после эмодзи или выделите его"}</p>
      </div>
      <div className="custom-emoji-text-editor-shell">
        <div
          ref={editorRef}
          className="custom-emoji-text-editor"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          aria-multiline="true"
          data-placeholder={placeholder}
          style={{ minHeight: `${Math.max(2, rows) * 1.45 + 1.4}em` }}
          onPaste={(event) => {
            event.preventDefault();
            pastePlainText(event.clipboardData.getData("text/plain"));
          }}
          onInput={emit}
          onKeyUp={refreshSelectionHint}
          onPointerUp={refreshSelectionHint}
          onFocus={refreshSelectionHint}
        />
      </div>
      <small>{plainLength} символов</small>
      {pickerOpen && <CustomEmojiPicker
        sets={library.sets}
        recent={library.recent}
        targetFallback={replacementTargetRef.current?.target.fallback}
        onSelect={selectEmoji}
        onClose={() => {
          replacementTargetRef.current = null;
          setSelectionFallback(null);
          setPickerOpen(false);
        }}
        onImport={() => {
          telegramWebApp()?.openTelegramLink?.("https://t.me/THLS_AllPrice_bot?start=emoji_import");
        }}
        loadPreview={library.loadPreview}
      />}
    </div>
  );
}
